import { AuditDetails, Entity, EntityLink, ItemLinkEntity } from '../entity';
import { AccountingResult } from '../accounting';
import { TaxInclusiveOption, PurchaseOrderLine } from '../purchase';
import { Attachment, Address, CodeStatus, CodeUoM, CodeType, CodeInvoiceStatus } from '../shared';
import { TaxRate } from '../taxrate';
import { WorkOrder, Job } from '../job';
import { Company } from '../tenant';
import { InvoiceWebHookSource } from '../webhooks';
import { Contact, CreditOptions, InvoiceOptions } from '../contact';
import { Payment } from '../payment';


export enum InvoiceDateOption {
    CurrentDate = 'Current Date',
    CompletedDate = 'Completed Date',
    FinalisedDate = 'Finalised Date'
}

export enum InvoiceStatus {
    Draft = 'Draft',
    Approved = 'Approved',
    Void = 'Void',
    Error = 'Error'
}

export enum InvoiceProcessingType {
    Draft = 'Draft',
    Approved = 'Approved',
    ApprovedAndSend = 'ApprovedAndSend',
    Void = 'Void',
    Error = 'Error',
    Reissue = 'Reissue',
    Sync = 'Sync',
    UpdateAndRecalculate = 'UpdateAndRecalculate'
}

export enum InvoiceDisplayMode {
    Job = 'Job',
    Global = 'Global',
    WorkOrder = 'WorkOrder'
}

export enum InvoiceType {
    Invoice = 'Invoice',

    PrepaymentInvoice = 'Prepayment Invoice',
    PrepaymentSupplier = 'Prepayment Supplier',

    CreditNote = 'Credit Note',
    CreditNoteSupplier = 'Credit Note Supplier',

    Bill = 'Bill',
    SupplierBill = 'Supplier Bill',
}


export type InvoiceBuildType = 'Manual' | 'Job' | 'Stage';
export type BillBuildType = 'Manual' | 'SuppliedByContractor' | 'NotSuppliedByContractor' | 'PrepaymentSupplier' | 'PurchaseOrder';

export interface InvoiceLine {
    _id: string;
    name: string;
    description: string;
    quantity: number;
    price: number;
    tax: number;
    subTotal: number;
    total: number;
    location?: string;
    notes?: string;
    codeStatus?: CodeStatus;
    uom?: string;
    codeUoM?: CodeUoM;
    itemLink?: ItemLinkEntity;
    links?: ItemLinkEntity[];

    glCode?: string;
    taxRate?: TaxRate;
    type?: CodeType;
    codeInvoicingStatus?: CodeInvoiceStatus;
}

export interface InvoiceContact extends Entity {
    address: Address;
    phone: string;
    email: string;
    type: 'Customer' | 'Technician' | 'Supplier';
    contactName: string;
    contactId: string;
    friendlyName: string;
    originalAccount?: Entity;

    invoiceTheme?: Entity;
    billTheme?: Entity;
    credit?: CreditOptions;
    invoiceOptions?: InvoiceOptions;
    taxId?: string;
}

export interface InvoiceBillingCompany extends Entity {
    address: Address;
    phone: string;
    email: string;
    taxRate: number;
    taxId: string;
    invoiceDetails?: string;
}

export interface InvoicePatch {
    active: boolean;
    dueDate: Date;
    generatedDate?: Date;
    lastIssuedDate?: Date;
    customerReference: string;
    notes: string;
    status: InvoiceStatus;
    integration?: AccountingResult;
    lines: InvoiceLine[];
    taxInclusiveOption: TaxInclusiveOption;
    taxRate: TaxRate;
    attachments?: Attachment[];
    terms: string;
    subTotal?: number;
    total?: number;
    tax?: number;
    timezone?: string;
    jobAddress?: Address;
    modifiedAfterSend?: boolean;
    source?: InvoiceWebHookSource;

    amountPaid?: number;
    amountRefunded?: number;

    lastPaymentDate?: Date;
    roundingAmount?: number;
    isReconcile?: boolean;
    outstandingAmount?: number;
    creditAmount?: number;
}

export interface InvoiceBase extends AuditDetails {
    _id: string;
    invoiceNumber: string;
    invoiceDate: Date;
    contact: InvoiceContact;
    billingCompany: InvoiceBillingCompany;
    job: Entity;
    workOrder?: Entity;
    version?: number;
    resources?: InvoiceContact[];
    timesheetIds?: string[];
    pricebook?: Entity;
    theme?: Entity;

    type?: InvoiceType;

    // no save to db, should using difference interface for display purpose
    hasPayments?: boolean;
    hasAllocation?: boolean;
    payments?: Payment[];
    links?: EntityLink[];

    pdf?: string;
}

export interface PrepaymentInvoicePatch {
    prepaymentSubTotal?: number;
    prepaymentTotal?: number;
    prepaymentTax?: number;
}

export interface Invoice extends InvoiceBase, InvoicePatch, PrepaymentInvoicePatch {
    isBulkInvoice?: boolean;
}


export interface Bill extends InvoiceBase, InvoicePatch, PrepaymentInvoicePatch {
}


export interface QuotePrepaymentInvoiceSummary extends Pick<Invoice,
    '_id' |
    'invoiceNumber' |
    'invoiceDate' |
    'prepaymentTotal' |
    'amountPaid'
> {
}


export interface InvoiceDownload extends Invoice {
    job: Job;
    customer: Contact;
    company: Company;
}

export interface BillDownload extends Bill {
    workOrder: WorkOrder;
    job: Job;
    technician: Contact;
    company: Company;
}

export interface InvoiceBaseEntity extends Entity {
    jobNumber: string;
    jobId: string;
    workOrderNumber: string;
    workOrderId: string;
}

export interface InvoicingSummary extends Pick<
    Invoice,
    '_id' | 'status' | 'invoiceNumber' | 'invoiceDate' |
    'dueDate' | 'total' | 'subTotal' | 'version' |
    'jobAddress' | 'timezone' | 'amountPaid' | 'outstandingAmount' |
    'type' | 'prepaymentSubTotal' | 'prepaymentTotal' | 'creditAmount' | 'lastPaymentDate' |
    'roundingAmount' | 'isReconcile'
> {
    integration?: { success: boolean, type: string, integrationInvoiceId: string };

    billingCompany?: string;
    job?: string;
    workOrder?: string;

    contact: string;
    contactId?: string;
    jobId?: string;
    workOrderId?: string;
    company?: Company;
    address: Address;

}

export interface LineAccounting {
    lines: PurchaseOrderLine[] | InvoiceLine[];
    subTotal: number;
    tax: number;
    total: number;
    supplierMarkup?: number;
}


export interface InvoiceLinePriceRequest {
    buyPriceId?: string;
    sellPriceId?: string;

    line: InvoiceLine;
}